//
// ============================================================================
// IMPORTS
// ============================================================================
//

import { getCurrentInstance, onUnmounted } from "vue";
import { useSessionState } from "./useSessionState";

//
// ============================================================================
// COMPOSABLE
// ============================================================================
//

/**
 * Page-Scoped State with Navigation Cleanup
 *
 * Creates reactive state that persists across page reloads but clears when
 * navigating away from the page. Combines the benefits of sessionStorage
 * persistence (survives F5/refresh) with automatic cleanup on component unmount
 * (navigation to other pages).
 *
 * Responsibilities:
 * - Persist state to sessionStorage for reload resilience
 * - Automatically clear state when component unmounts (navigation)
 * - Remove sessionStorage entry on cleanup to prevent stale data
 * - Maintain SSR compatibility via useSessionState
 *
 * @param key - Unique storage key (e.g., "validation.search")
 * @param initialValue - Default value when state is not persisted
 * @returns Reactive ref that auto-syncs to sessionStorage with cleanup
 */
export function usePageScopedState<T>(key: string, initialValue: T) {
  //
  // ============================================================================
  // STATE
  // ============================================================================
  //

  // Use sessionState for auto-sync to sessionStorage
  const state = useSessionState(key, initialValue);

  //
  // ============================================================================
  // LIFECYCLE HOOKS
  // ============================================================================
  //

  // Clear on unmount if in component context (navigation away from page)
  if (getCurrentInstance()) {
    onUnmounted(() => {
      // Reset to initial value
      state.value = initialValue;

      // Remove from sessionStorage to prevent stale data on return
      if (import.meta.client) {
        try {
          sessionStorage.removeItem(key);
        } catch {
          // Storage blocked or unavailable - ignore
        }
      }
    });
  }

  return state;
}
